import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CalendarClock, Coins, Heart, Megaphone, UserCheck, Wallet } from "lucide-react";
import { ReadableCard, ReadableNavButton } from "../Sticker";
import LinkifiedText from "../LinkifiedText";
import { eventPublicPath } from "../../lib/eventLinks";
import { classPublicHref } from "../../lib/classLinks";
import {
  getUnreadNotifications,
  isNotificationRead,
  markAllNotificationsReadLocal,
  markNotificationReadLocal,
} from "../../lib/notificationReads";
import { groupBroadcastsByOccurrence, sectionLabelForGroup } from "../../lib/broadcastSort";
import PushNotificationPromptRow from "./PushNotificationPromptRow";
import { shouldOfferPush } from "../../lib/webPush";

const ROW_ACTION_CLASS = "min-h-0 py-1.5 px-3 text-[13px]";
const HEADER_ACTION_CLASS = "min-h-0 py-1.5 px-3 text-[13px]";

const SCHOOL_LINK_TYPES = new Set([
  "class_rsvp_reminder",
  "class_instance_cancelled",
  "payment_reviewed",
  "credit_adjusted",
  "role_assigned",
  "expiry_nudge",
]);

function typeMeta(type) {
  if (type === "event_change") {
    return { label: "Schedule update", Icon: CalendarClock };
  }
  if (type === "favourite_rsvp_reminder") {
    return { label: "RSVP reminder", Icon: Heart };
  }
  if (type === "class_rsvp_reminder") {
    return { label: "Class reminder", Icon: Heart };
  }
  if (type === "class_instance_cancelled") {
    return { label: "Class cancelled", Icon: CalendarClock };
  }
  if (type === "host_broadcast") {
    return { label: "Host message", Icon: Megaphone };
  }
  if (type === "invite_reviewed") {
    return { label: "Invite update", Icon: UserCheck };
  }
  if (type === "payment_reviewed") {
    return { label: "Fee update", Icon: Wallet };
  }
  if (type === "credit_adjusted") {
    return { label: "Credits", Icon: Coins };
  }
  if (type === "role_assigned") {
    return { label: "School role", Icon: UserCheck };
  }
  if (type === "expiry_nudge") {
    return { label: "Pass expiring", Icon: CalendarClock };
  }
  return { label: "Notification", Icon: Bell };
}

function notificationPath(item) {
  if (SCHOOL_LINK_TYPES.has(item.type)) {
    return item.href || classPublicHref({
      schoolSlug: item.school_slug,
      classSlug: item.class_slug,
    }, { date: item.occurrence_date || item.date }) || (item.school_slug ? `/school/${item.school_slug}` : "/");
  }
  if (item.event_id) {
    return eventPublicPath(
      { id: item.event_id, city_slug: item.city_slug, public_slug: item.public_slug },
      { occurrence_date: item.occurrence_date },
    );
  }
  return item.href || "/";
}

function NotificationRow({ item, onRead, readTick, dancerId }) {
  const read = isNotificationRead(item);
  void readTick;
  void dancerId;
  const { label, Icon } = typeMeta(item.type);
  const contextLine = [item.event_name, item.host_label?.trim()].filter(Boolean).join(" · ");
  const eventPath = notificationPath(item);
  const showViewLink = eventPath && eventPath !== "/" && (
    item.type === "invite_reviewed"
    || item.type === "payment_reviewed"
    || item.type === "credit_adjusted"
    || item.type === "role_assigned"
    || item.type === "expiry_nudge"
    || item.type === "class_instance_cancelled"
  );

  function handleRead() {
    if (read) return;
    markNotificationReadLocal(item.id);
    onRead(item.id);
  }

  return (
    <ReadableCard
      className={`p-0 overflow-hidden ${read ? "bg-white" : "bg-plum/5 cursor-pointer"}`}
      data-testid={`notification-row-${item.id}`}
      onClick={handleRead}
    >
      <div className="p-4 min-w-0">
        <p className="font-ui text-[12px] font-semibold text-ink/55 inline-flex items-center gap-1.5 mb-1">
          <Icon className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden />
          {label}
        </p>
        <p className="font-ui text-[14px] font-medium text-ink/80 min-w-0 break-words [overflow-wrap:anywhere] whitespace-pre-line">
          <LinkifiedText>{item.message}</LinkifiedText>
        </p>
        <p className="font-ui text-[12px] font-medium text-ink/55 mt-1">
          {item.created_at
            ? new Date(item.created_at).toLocaleString("en-GB", {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })
            : ""}
        </p>
        {contextLine && (
          <p className="font-ui text-[12px] font-medium text-ink/55 mt-1 min-w-0 break-words [overflow-wrap:anywhere]">
            {contextLine}
          </p>
        )}
      </div>
      <div className="bg-white border-t border-black/[0.08] px-4 py-3 flex flex-wrap gap-2">
        {!read && (
          <ReadableNavButton
            type="button"
            variant="default"
            onClick={(e) => {
              e.stopPropagation();
              handleRead();
            }}
            className={ROW_ACTION_CLASS}
            data-testid={`notification-mark-read-${item.id}`}
          >
            Mark as read
          </ReadableNavButton>
        )}
        {item.type === "favourite_rsvp_reminder" && (
          <ReadableNavButton
            as={Link}
            to={eventPath}
            variant="primary"
            className={ROW_ACTION_CLASS}
            onClick={(e) => e.stopPropagation()}
            data-testid={`notification-rsvp-${item.id}`}
          >
            RSVP now
          </ReadableNavButton>
        )}
        {item.type === "class_rsvp_reminder" && (
          <ReadableNavButton
            as={Link}
            to={eventPath}
            variant="primary"
            className={ROW_ACTION_CLASS}
            onClick={(e) => e.stopPropagation()}
            data-testid={`notification-class-rsvp-${item.id}`}
          >
            RSVP now
          </ReadableNavButton>
        )}
        {showViewLink && (
          <ReadableNavButton
            as={Link}
            to={eventPath}
            variant="default"
            className={ROW_ACTION_CLASS}
            onClick={(e) => e.stopPropagation()}
            data-testid={`notification-view-${item.id}`}
          >
            {item.type === "class_instance_cancelled" ? "View class" : "View"}
          </ReadableNavButton>
        )}
      </div>
    </ReadableCard>
  );
}

export default function NotificationsTab({ notifications, readTick, onRead, onReadAll, dancerId }) {
  const [pushPromptDismissed, setPushPromptDismissed] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [pushCheckDone, setPushCheckDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPushCheckDone(false);
    (async () => {
      if (!dancerId || pushPromptDismissed) {
        if (!cancelled) {
          setShowPushPrompt(false);
          setPushCheckDone(true);
        }
        return;
      }
      const offer = await shouldOfferPush();
      if (!cancelled) {
        setShowPushPrompt(offer);
        setPushCheckDone(true);
      }
    })();
    return () => { cancelled = true; };
  }, [dancerId, pushPromptDismissed]);

  const groups = useMemo(() => groupBroadcastsByOccurrence(notifications), [notifications]);
  const unreadIds = useMemo(
    () => getUnreadNotifications(notifications).map((n) => n.id),
    [notifications, readTick],
  );

  function handleReadAll() {
    markAllNotificationsReadLocal(unreadIds);
    onReadAll(unreadIds);
  }

  if (pushCheckDone && !showPushPrompt && !notifications.length) {
    return (
      <div className="max-w-page mx-auto px-4 mt-8">
        <ReadableCard className="p-6 text-center bg-cream">
          <p className="font-ui font-semibold text-[16px] leading-snug">No notifications right now.</p>
          <p className="font-ui font-medium text-[14px] text-ink/70 mt-2">
            You&apos;ll hear about schedule changes, invites, fees, and RSVP reminders.
          </p>
        </ReadableCard>
      </div>
    );
  }

  return (
    <div className="max-w-page mx-auto px-4 mt-8 space-y-8 pb-4" data-testid="feed-notifications-tab">
      {showPushPrompt && (
        <section className="space-y-3" data-testid="notification-group-push-prompt">
          <PushNotificationPromptRow
            dancerId={dancerId}
            onEnabled={() => setShowPushPrompt(false)}
            onDismiss={() => setPushPromptDismissed(true)}
          />
        </section>
      )}

      {notifications.length > 0 && unreadIds.length > 0 && (
        <div className="flex justify-end">
          <ReadableNavButton
            type="button"
            variant="default"
            onClick={handleReadAll}
            className={HEADER_ACTION_CLASS}
            data-testid="notification-mark-all-read"
          >
            Mark all as read
          </ReadableNavButton>
        </div>
      )}

      {notifications.length > 0 && groups.map((group) => (
        <section key={group.key} className="space-y-3" data-testid={`notification-group-${group.key}`}>
          <header>
            <h2 className="font-ui font-semibold text-[18px] sm:text-[20px] leading-tight">
              {sectionLabelForGroup(group)}
            </h2>
            <p className="font-ui font-medium text-[14px] text-ink/70 mt-0.5">{group.event_name}</p>
          </header>
          <div className="flex flex-col gap-3">
            {group.items.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                readTick={readTick}
                dancerId={dancerId}
                onRead={onRead}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
